import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { Caption, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { type ComponentProps, type HTMLAttributes, type MouseEventHandler, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryBidCount,
    getItemSummaryBuyNowPrice,
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogStatus,
    getItemSummaryCurrency,
    getItemSummaryIsAvailable,
    getItemSummaryIsCatalogOnly,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsReserveMet,
    getItemSummaryIsSold,
    getItemSummaryLeadingBid,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
    getItemSummaryUrl,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import noop from '@/utils/noop';
import { getIsSearchPage } from '@/utils/urls';

export type ItemCardPriceRowProps = {
    className?: string;
    isHovered?: boolean;
    itemId: number;
    onMouseEnter?: MouseEventHandler<HTMLSpanElement>;
    onMouseLeave?: MouseEventHandler<HTMLSpanElement>;
    onNavigate?: () => void;
    onUserLoggedIn?: () => void;
    showBidCount?: boolean;
};

const PRICE_LINK_TEXT_HOVER_CLASSES =
    '[&_span]:!text-link-hover [&_span]:!underline [&_span]:!cursor-pointer [&_div]:!text-link-hover [&_div]:!underline [&_div]:!cursor-pointer';

export function ItemCardPriceRow({
    className,
    isHovered,
    itemId,
    onMouseEnter = noop,
    onMouseLeave = noop,
    onNavigate,
    showBidCount,
}: ItemCardPriceRowProps) {
    const formatMessage = useAppFormatMessage();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const buyNowPrice = useAppSelector((state) => getItemSummaryBuyNowPrice(state, itemId));
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const isAvailable = useAppSelector((state) => getItemSummaryIsAvailable(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const isReserveMet = useAppSelector((state) => getItemSummaryIsReserveMet(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const itemUrl = useAppSelector((state) => getItemSummaryUrl(state, itemId));
    const itemHref = useLocalizedPath(itemUrl);

    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const isOnSearchPage = getIsSearchPage(pathname);

    const isDone = catalogStatus === 'done';
    const buyNowAvailable = buyNowStatus === BuyNowStatus.Available && isDone;
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const currentPrice = leadingBid || salePrice || startPrice || 0;

    const spanProps: HTMLAttributes<HTMLSpanElement> = {
        onMouseEnter,
        onMouseLeave,
    };

    let returnComponent: ReactNode = null;
    const itemPriceGatewayContainerProps: ComponentProps<typeof ItemPriceGatewayContainer> = {
        hideIcon: true,
        itemId,
        onMouseEnter,
        onMouseLeave,
        runFunction: () => {
            onNavigate?.();
            navigate({ pathname: itemUrl });
        },
    };

    if (!isDone && isPaused) {
        returnComponent = (
            <ItemPriceGatewayContainer {...itemPriceGatewayContainerProps}>
                <span {...spanProps}>
                    <div className="flex items-start text-text-primary">
                        <H5 className="flex items-start text-text-primary">{formatMessage(['lotWithdrawn'])}</H5>
                    </div>
                </span>
            </ItemPriceGatewayContainer>
        );
    } else if (isLotOpen || buyNowAvailable || (isAvailable && !isUnsold && !isPaused && !isCatalogOnly)) {
        returnComponent = (
            <span {...spanProps}>
                <SubtleMonochromeLink
                    href={itemHref}
                    onClick={() => onNavigate?.()}
                >
                    <div
                        className={cn('flex items-start text-text-primary', isHovered && PRICE_LINK_TEXT_HOVER_CLASSES)}
                    >
                        <H5 className="flex items-start text-text-primary">
                            <FormattedCurrency
                                convert
                                noFraction
                                sourceCurrency={currency}
                                // current price unless buy now available PLUS is not lot open (preserves old order of operations)
                                value={buyNowAvailable && !isLotOpen ? buyNowPrice : currentPrice}
                            />
                        </H5>
                        {showBidCount && !buyNowAvailable && (
                            <Caption className="ml-xs h-full">
                                {formatMessage(['itemBidding', 'bid'], { values: { bidCount } })}
                            </Caption>
                        )}
                    </div>
                </SubtleMonochromeLink>
            </span>
        );
    } else if (isSold && isReserveMet) {
        returnComponent = (
            <ItemPriceGatewayContainer {...itemPriceGatewayContainerProps}>
                <span {...spanProps}>
                    <div
                        className={cn('flex items-start text-text-primary', isHovered && PRICE_LINK_TEXT_HOVER_CLASSES)}
                    >
                        <H5 className="flex items-start text-text-primary">
                            {salePrice
                                ? formatMessage(['sold_for'], {
                                      values: {
                                          amount: (
                                              <FormattedCurrency
                                                  key="sold-for-amount"
                                                  noFraction
                                                  sourceCurrency={currency}
                                                  value={salePrice}
                                              />
                                          ),
                                      },
                                  })
                                : formatMessage(['contact_seller_for_price'])}
                        </H5>
                    </div>
                </span>
            </ItemPriceGatewayContainer>
        );
    } else if (isSold && !isReserveMet) {
        returnComponent = (
            <ItemPriceGatewayContainer
                {...itemPriceGatewayContainerProps}
                runFunction={undefined}
            >
                <span {...spanProps}>
                    <SubtleMonochromeLink
                        href={itemHref}
                        onClick={() => onNavigate?.()}
                    >
                        <div
                            className={cn(
                                'flex items-start text-text-primary',
                                isHovered && PRICE_LINK_TEXT_HOVER_CLASSES
                            )}
                        >
                            <H5 className="flex items-start text-text-primary">
                                {(isCatalogOnly || isOnSearchPage) && formatMessage(['lot_closed'])}
                            </H5>
                        </div>
                    </SubtleMonochromeLink>
                </span>
            </ItemPriceGatewayContainer>
        );
    } else if (isCatalogOnly && !isPassed) {
        returnComponent = (
            <span {...spanProps}>
                <SubtleMonochromeLink
                    href={itemHref}
                    onClick={() => onNavigate?.()}
                >
                    <div
                        className={cn('flex items-start text-text-primary', isHovered && PRICE_LINK_TEXT_HOVER_CLASSES)}
                    >
                        <H5 className="flex items-start text-text-primary">
                            {formatMessage(['registration', 'preview_only'])}
                        </H5>
                    </div>
                </SubtleMonochromeLink>
            </span>
        );
    } else if (isUnsold) {
        returnComponent = (
            <ItemPriceGatewayContainer {...itemPriceGatewayContainerProps}>
                <span {...spanProps}>
                    <div
                        className={cn('flex items-start text-text-primary', isHovered && PRICE_LINK_TEXT_HOVER_CLASSES)}
                    >
                        <H5 className="flex items-start text-text-primary">{formatMessage(['lot_unsold_pending'])}</H5>
                    </div>
                </span>
            </ItemPriceGatewayContainer>
        );
    } else if (isPassed) {
        returnComponent = (
            <ItemPriceGatewayContainer {...itemPriceGatewayContainerProps}>
                <span {...spanProps}>
                    <div
                        className={cn('flex items-start text-text-primary', isHovered && PRICE_LINK_TEXT_HOVER_CLASSES)}
                    >
                        <H5 className="flex items-start text-text-primary">
                            {isCatalogOnly
                                ? formatMessage(['lot_closed'])
                                : formatMessage(['itemBidding', 'passedText'])}
                        </H5>
                    </div>
                </span>
            </ItemPriceGatewayContainer>
        );
    }

    return <section className={className}>{returnComponent}</section>;
}
